import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import {
  MdAdd,
  MdContentCopy,
  MdEdit,
  MdUpload,
  MdDownload,
  MdClose,
  MdCheckCircle
} from 'react-icons/md';
import DataTable from '../../components/ui/DataTable';
import { get_all_staff, duplicate_staff, upload_staff_csv } from '../../api/staffAPI';
import Button from '../../components/ui/Button';
import FileUploadField from '../../components/FileUploadField';
import notify from '../../utils/IANotification';

export default function StaffPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const [staffData, setStaffData] = useState([])
  const [loading, setLoading] = useState(true)

  // CSV Import State
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)

  useEffect(() => {
    fetchStaff()
  }, [])

  const fetchStaff = async () => {
    setLoading(true)
    const data = await get_all_staff()
    if (data?.status === 200) {
      setStaffData(data.data || [])
    }
    setLoading(false)
  }

  const downloadSampleCsv = () => {
    const sampleRows = [
      "Name,Role,Title,Chinese Name,Email,Bio,Order",
      "Rev Dr Aaron Tay,Pastor,Pastor-in-Charge,郑韶强牧师,aaron.tay@bmc.org.sg,\"Pastor-in-Charge of Bedok Methodist Church overseeing overall pastoral leadership and ministry direction.\",1",
      "Rev David Gwee,Pastor,Associate Pastor,魏得胜牧师,david.gwee@bmc.org.sg,\"Associate Pastor overseeing Outreach, Missions and Discipleship.\",2",
      "Timothy Lee,Staff,Youth Ministry Staff,李志远,timothy.lee@bmc.org.sg,\"Serving in Youth Ministry, youth worship and mentoring young disciples.\",3",
      "Eileen Tan,Staff,Admin Executive,陈爱玲,eileen.tan@bmc.org.sg,\"Church administration, communications and facility operations support.\",4"
    ]
    const content = sampleRows.join("\n")
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'sample_church_staff.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFileSelected = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        setCsvText(e.target.result || '')
      }
      reader.readAsText(file)
    }
  }

  const handleCsvUpload = async () => {
    if (!csvText.trim()) {
      notify({ type: "error", message: "Please select or paste CSV content first" })
      return
    }

    setCsvUploading(true)
    setCsvResult(null)

    const res = await upload_staff_csv(csvText)
    setCsvUploading(false)

    if (res?.status === 200) {
      setCsvResult({ success: true, message: res.message || "Staff imported successfully!" })
      notify({ type: "success", message: res.message || "Staff imported successfully!" })
      fetchStaff()
      setTimeout(() => {
        setShowCsvModal(false)
        setCsvText('')
        setCsvResult(null)
      }, 1600)
    } else {
      setCsvResult({ success: false, message: res?.message || "Failed to import staff CSV" })
      notify({ type: "error", message: res?.message || "Failed to import staff CSV" })
    }
  }

  const table_columns = [
    {
      header: "Order",
      accessor: "seq_order",
      modifier: (val) => <span className="font-mono text-xs text-gray-500 font-semibold">#{val || 0}</span>
    },
    {
      header: "Name",
      accessor: "name",
      modifier: (val, row) => (
        <div>
          <span className="font-semibold text-gray-900">{val}</span>
          {row.chinese_name && <span className="ml-2 text-xs text-gray-500 font-normal">({row.chinese_name})</span>}
        </div>
      )
    },
    {
      header: "Role",
      accessor: "role",
      modifier: (val) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
          val === 'Pastor'
            ? 'bg-purple-100 text-purple-800'
            : 'bg-emerald-100 text-emerald-800'
        }`}>
          {val}
        </span>
      )
    },
    {
      header: "Title / Position",
      accessor: "title"
    },
    {
      header: "Email",
      accessor: "email",
      modifier: (val) => val ? <span className="text-xs text-gray-600 font-mono">{val}</span> : <span className="text-gray-400 text-xs italic">-</span>
    }
  ]

  return (
    <AdminLayout
      navSelected={"Church Staff"}
      toolbar={
        <>
          <h1 className='text-lg font-semibold text-gray-900'>Church Staff &amp; Pastors</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={MdUpload}
              onClick={() => {
                setCsvText('')
                setCsvResult(null)
                setShowCsvModal(true)
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                navigate("/admin/staff/details?staff_id=new")
              }}
            >
              New Staff Member
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="flex-row items-center flex justifyCenter w-100 p-12">
          <h1 className="text-gray-500 font-medium">Loading staff...</h1>
        </div>
      ) : (
        <div className="w-100">
          <DataTable
            columns={table_columns}
            data={staffData}
            enableActions
            actions={(row) => {
              return (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={MdContentCopy}
                    onClick={async () => {
                      const res = await duplicate_staff(row.id)
                      if (res?.status === 200) {
                        notify({ message: "Staff member duplicated", type: "success" })
                        fetchStaff()
                      } else {
                        notify({ message: "Failed to duplicate staff member", type: "error" })
                      }
                    }}
                  >
                    Duplicate
                  </Button>
                  <Button variant="outline" size="sm" icon={MdEdit} onClick={() => navigate(`/admin/staff/details?staff_id=${row.id}`)}>
                    Edit
                  </Button>
                </div>
              )
            }}
          />
        </div>
      )}

      {/* CSV Import Modal (Adapted from Missions with Add, Update, Replace modes) */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4 max-h-[92vh] overflow-y-auto">
            <button
              onClick={() => setShowCsvModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <MdClose size={22} />
            </button>

            <div className="flex items-center justify-between pr-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Staff Information</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Upload a CSV file or paste content below. Headers supported: <code className="bg-gray-100 px-1 rounded text-gray-800 font-mono">Name, Role, Title, Chinese Name, Email, Bio, Order</code>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={MdDownload}
                onClick={downloadSampleCsv}
              >
                Sample CSV
              </Button>
            </div>

            {/* Informational Upsert Notice */}
            <div className="p-3 bg-blue-50/70 rounded-xl border border-blue-100 text-xs text-blue-800 flex items-start gap-2">
              <span className="font-semibold shrink-0">Automatic Upsert:</span>
              <span>The system will automatically update existing staff members (matched by Email or Name) or add them as new members if they do not exist.</span>
            </div>

            {/* File Upload Component */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <FileUploadField
                item_index="staff_csv"
                max_file_size={5}
                max_files={1}
                text="Select or Drag Staff CSV File"
                subtext="Supported format: .csv"
                onChange={(files) => handleFileSelected(files)}
              />
            </div>

            {/* Text Preview / Edit Area */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CSV Content Preview / Manual Edit:
              </label>
              <textarea
                rows={6}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`Name,Role,Title,Chinese Name,Email,Bio,Order\nRev Dr Aaron Tay,Pastor,Pastor-in-Charge,郑韶强牧师,aaron.tay@bmc.org.sg,"Pastor-in-Charge...",1`}
                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {csvResult && (
              <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                <MdCheckCircle size={18} />
                {csvResult.message}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                icon={MdUpload}
                onClick={handleCsvUpload}
                disabled={csvUploading || !csvText.trim()}
              >
                {csvUploading ? 'Processing...' : 'Process & Import Staff'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}